'use client'

import { useEffect, useRef } from 'react'

const STORAGE_KEY = 'kettle-booking-last-activity'
const EXPIRATION_MS = 60 * 60 * 1000 // 60 minutes
const CHECK_INTERVAL_MS = 60 * 1000 // check every 1 minute
const THROTTLE_MS = 60 * 1000 // throttle activity writes to every 1 minute

function getLastActivity(): number | null {
  if (typeof window === 'undefined') return null
  const stored = localStorage.getItem(STORAGE_KEY)
  return stored ? parseInt(stored, 10) : null
}

function setLastActivity(): void {
  if (typeof window === 'undefined') return
  localStorage.setItem(STORAGE_KEY, Date.now().toString())
}

function clearLastActivity(): void {
  if (typeof window === 'undefined') return
  localStorage.removeItem(STORAGE_KEY)
}

function isSessionExpired(): boolean {
  const lastActivity = getLastActivity()
  if (lastActivity === null) return false
  return Date.now() - lastActivity > EXPIRATION_MS
}

interface UseSessionExpirationOptions {
  onExpired: () => void
  hasHydrated: boolean
}

export function useSessionExpiration({
  onExpired,
  hasHydrated,
}: UseSessionExpirationOptions): void {
  const expiredRef = useRef(false)

  // Check for expiration on mount and periodically
  useEffect(() => {
    if (!hasHydrated || expiredRef.current) return

    // Check immediately on mount
    if (isSessionExpired()) {
      expiredRef.current = true
      clearLastActivity()
      onExpired()
      return
    }

    // Session is still valid (or new) — update the timestamp
    setLastActivity()

    // Periodically check for expiration while the user is on the page
    const interval = setInterval(() => {
      if (expiredRef.current) return
      if (isSessionExpired()) {
        expiredRef.current = true
        clearLastActivity()
        clearInterval(interval)
        onExpired()
      }
    }, CHECK_INTERVAL_MS)

    return () => clearInterval(interval)
  }, [hasHydrated, onExpired])

  // Track user activity to update the last activity timestamp
  useEffect(() => {
    if (!hasHydrated || expiredRef.current) return

    const events: (keyof WindowEventMap)[] = [
      'click',
      'keydown',
      'scroll',
      'mousemove',
      'touchstart',
    ]

    let lastUpdate = Date.now()
    const throttledUpdate = (): void => {
      if (expiredRef.current) return
      const now = Date.now()
      if (now - lastUpdate > THROTTLE_MS) {
        lastUpdate = now
        setLastActivity()
      }
    }

    events.forEach((event) => window.addEventListener(event, throttledUpdate, { passive: true }))

    return () => {
      events.forEach((event) => window.removeEventListener(event, throttledUpdate))
    }
  }, [hasHydrated])
}
